import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Textarea } from "../ui/Textarea";
import DriverSelector from "./DriverSelector";
import DriverPhoneEditor from "./DriverPhoneEditor";
import {
  createDriverContact,
  getDriverPhones,
  newIdempotencyKey,
  sendDriverMessage,
} from "../../api/helpdeskDrivers";
import { useCountry } from "../../context/CountryContext";
import { driverMessagingErrorText } from "../../utils/driverMessagingErrors";
import { defaultDialCode, toE164 } from "../../utils/driverPhone";
import { cn } from "../../utils/cn";

export const MAX_MESSAGE_LENGTH = 4096;
const MAX_ALIAS_LENGTH = 120;

const INPUT_CLS =
  "w-full !h-11 md:!h-8 !px-2.5 !py-0 !text-base md:!text-sm !rounded-md !border !border-border !bg-bg text-fg placeholder:!text-fg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50";
const BTN =
  "inline-flex items-center justify-center h-11 md:h-8 px-3 rounded-md text-xs font-medium border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
const BTN_SECONDARY = cn(BTN, "border-border bg-transparent text-fg hover:bg-bg-subtle");
// hd-brand-solid: white on brand-500 is 2,80:1 (helpdeskMobile.scss).
const BTN_PRIMARY = cn(BTN, "hd-brand-solid border-transparent bg-brand-500 text-white hover:bg-brand-600");
const BTN_LINK =
  "text-xs font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50 disabled:no-underline";

const VIEW = { COMPOSE: "compose", ADD_NUMBER: "addNumber", EDIT_PHONE: "editPhone" };

/** Registrado primero; el resto en el orden del backend. */
function pickDefaultPhone(phones) {
  return (phones.find((p) => p.source === "registered") || phones[0])?.phone || "";
}

/**
 * Formulario "Agregar número" (POST helpdesk/contacts). El prefijo del país se
 * antepone solo cuando es inequívoco (ver utils/driverPhone).
 */
function AddNumberForm({ driverId, dialCode, onAdded, onCancel }) {
  const { t } = useTranslation();
  const phoneId = useId();
  const aliasId = useId();
  const errorId = useId();
  const phoneRef = useRef(null);
  const savingRef = useRef(false);
  const [phone, setPhone] = useState("");
  const [alias, setAlias] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    phoneRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (savingRef.current) return;
    const e164 = toE164(phone, dialCode);
    if (!e164) {
      setError(
        dialCode
          ? t("El teléfono no es válido. Revisa el número e incluye el código de país.")
          : t("Escribe el número completo con el código de país, por ejemplo +54 9 261 123 4567."),
      );
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    const result = await createDriverContact({
      phone: e164,
      driverId,
      alias: alias.trim() || undefined,
    });
    savingRef.current = false;
    setSaving(false);
    if (result.error) {
      setError(driverMessagingErrorText(result.error, t));
      return;
    }
    onAdded(e164);
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-3">
      <div>
        <label htmlFor={phoneId} className="block text-xs font-medium text-fg mb-1">
          {t("Número de WhatsApp")}
        </label>
        <div className="flex items-center gap-2">
          {dialCode && (
            <span className="text-sm text-fg-muted tabular-nums shrink-0" aria-hidden="true">
              {dialCode}
            </span>
          )}
          <input
            id={phoneId}
            ref={phoneRef}
            type="text"
            inputMode="tel"
            autoComplete="off"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              if (error) setError("");
            }}
            disabled={saving}
            placeholder={dialCode ? t("9 1234 5678") : t("+54 9 261 123 4567")}
            aria-describedby={error ? errorId : undefined}
            aria-invalid={error ? true : undefined}
            className={INPUT_CLS}
          />
        </div>
        <p className="mt-1 text-xs text-fg-muted">
          {dialCode
            ? t("Sin el código de país se usa {{dial}}. Para otro país, empieza con +.").replace(
                "{{dial}}",
                dialCode,
              )
            : t("Incluye el código de país, empezando con +.")}
        </p>
      </div>
      <div>
        <label htmlFor={aliasId} className="block text-xs font-medium text-fg mb-1">
          {t("Alias (opcional)")}
        </label>
        <input
          id={aliasId}
          type="text"
          autoComplete="off"
          maxLength={MAX_ALIAS_LENGTH}
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          disabled={saving}
          placeholder={t("Ej.: celular de trabajo")}
          className={INPUT_CLS}
        />
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-xs text-status-error">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving || !phone.trim()} aria-busy={saving} className={BTN_PRIMARY}>
          {saving ? t("Guardando…") : t("Guardar número")}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className={BTN_SECONDARY}>
          {t("Cancelar")}
        </button>
      </div>
    </form>
  );
}

/**
 * "Escribir a repartidor": elegir repartidor, elegir uno de sus números
 * (registrado o de agenda), redactar y enviar. Desde aquí también se agrega un
 * número y se edita el teléfono registrado.
 *
 * Idempotencia del envío: la clave se genera por intento y se REUTILIZA si el
 * usuario reintenta el mismo envío (mismo repartidor, número y texto) tras un
 * error; cambia solo si cambia alguno de los tres. Va en el body porque el CORS
 * del BFF no admite headers propios.
 *
 * Props:
 *   - open, onClose()
 *   - onSent({ conversationId|null, messageId|null, driver })  tras un envío OK
 *     (el modal ya se cerró)
 */
export default function WriteToDriverModal({ open, onClose, onSent }) {
  const { t } = useTranslation();
  const country = useCountry() || {};
  const dialCode = defaultDialCode(country.countryCode, country.resolved);

  const textId = useId();
  const counterId = useId();
  const errorId = useId();
  const legendId = useId();

  const [view, setView] = useState(VIEW.COMPOSE);
  const [driver, setDriver] = useState(null);
  const [phonesState, setPhonesState] = useState({ status: "idle", phones: [], error: "" });
  const [selectedPhone, setSelectedPhone] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  const sendingRef = useRef(false);
  const phonesRequestRef = useRef(0);
  const attemptRef = useRef({ signature: null, key: null });

  const loadPhones = useCallback(
    async (driverId, preferredPhone) => {
      const requestId = ++phonesRequestRef.current;
      setPhonesState({ status: "loading", phones: [], error: "" });
      setSelectedPhone("");
      const result = await getDriverPhones(driverId);
      // Cambió el repartidor mientras cargaba: la respuesta vieja se descarta.
      if (requestId !== phonesRequestRef.current) return;
      if (result.error) {
        setPhonesState({
          status: "error",
          phones: [],
          error: driverMessagingErrorText(result.error, t),
        });
        return;
      }
      const phones = Array.isArray(result.data?.phones) ? result.data.phones : [];
      setPhonesState({ status: "ready", phones, error: "" });
      const preferred = preferredPhone && phones.find((p) => p.phone === preferredPhone);
      setSelectedPhone(preferred ? preferred.phone : pickDefaultPhone(phones));
    },
    [t],
  );

  const handleDriverChange = (next) => {
    if (!next?.id || next.id === driver?.id) return;
    setDriver({ id: next.id, name: next.name });
    setSendError("");
    setView(VIEW.COMPOSE);
    loadPhones(next.id);
  };

  const trimmedText = text.trim();
  const overLimit = text.length > MAX_MESSAGE_LENGTH;
  const canSend =
    view === VIEW.COMPOSE &&
    !!driver &&
    !!selectedPhone &&
    trimmedText.length > 0 &&
    !overLimit &&
    !sending;

  const handleSend = async () => {
    if (!canSend || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError("");

    const signature = `${driver.id}|${selectedPhone}|${trimmedText}`;
    if (attemptRef.current.signature !== signature) {
      attemptRef.current = { signature, key: newIdempotencyKey() };
    }

    const result = await sendDriverMessage({
      driverId: driver.id,
      phone: selectedPhone,
      text: trimmedText,
      idempotencyKey: attemptRef.current.key,
    });
    sendingRef.current = false;
    setSending(false);

    if (result.error) {
      setSendError(driverMessagingErrorText(result.error, t));
      return;
    }
    attemptRef.current = { signature: null, key: null };
    onClose?.();
    onSent?.({
      conversationId: result.data?.conversationId ?? null,
      messageId: result.data?.messageId ?? null,
      driver,
    });
  };

  const backToCompose = () => setView(VIEW.COMPOSE);

  const handleNumberAdded = (phone) => {
    setView(VIEW.COMPOSE);
    loadPhones(driver.id, phone);
  };

  const handlePhoneEdited = (phone) => {
    setView(VIEW.COMPOSE);
    loadPhones(driver.id, phone);
  };

  const { status: phonesStatus, phones } = phonesState;

  const footer = (
    <>
      <button type="button" onClick={onClose} disabled={sending} className={BTN_SECONDARY}>
        {t("Cancelar")}
      </button>
      <button
        type="button"
        onClick={handleSend}
        disabled={!canSend}
        aria-busy={sending}
        className={BTN_PRIMARY}
      >
        {sending ? t("Enviando…") : t("Enviar")}
      </button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={sending ? undefined : onClose}
      title={t("Escribir a repartidor")}
      description={t("Envía un WhatsApp a un repartidor de tu operación.")}
      footer={footer}
    >
      <div className="space-y-4">
        <div>
          <span className="block text-xs font-medium text-fg mb-1">{t("Repartidor")}</span>
          <DriverSelector
            value={driver}
            onChange={handleDriverChange}
            disabled={sending}
            placeholder={driver?.name || t("Buscar repartidor…")}
          />
          {driver && (
            <p className="mt-1 text-xs text-fg-muted">
              {t("Repartidor seleccionado: {{name}}").replace("{{name}}", driver.name)}
            </p>
          )}
        </div>

        {driver && phonesStatus === "loading" && (
          <p role="status" className="text-xs text-fg-muted">
            {t("Cargando números…")}
          </p>
        )}

        {driver && phonesStatus === "error" && (
          <div role="alert" className="text-xs text-status-error">
            <span>{phonesState.error}</span>{" "}
            <button type="button" onClick={() => loadPhones(driver.id)} className="underline font-medium">
              {t("Reintentar")}
            </button>
          </div>
        )}

        {driver && phonesStatus === "ready" && view === VIEW.COMPOSE && (
          <fieldset className="space-y-2" disabled={sending}>
            <legend id={legendId} className="block text-xs font-medium text-fg mb-1">
              {t("Enviar a")}
            </legend>
            {phones.length === 0 && (
              <p className="text-xs text-fg-muted">
                {t("Este repartidor no tiene números. Agrega uno para poder escribirle.")}
              </p>
            )}
            {phones.map((p) => (
              <label
                key={p.phone}
                className="flex items-center gap-2 text-sm text-fg cursor-pointer min-h-[44px] md:min-h-0"
              >
                <input
                  type="radio"
                  name="write-to-driver-phone"
                  value={p.phone}
                  checked={selectedPhone === p.phone}
                  onChange={() => {
                    setSelectedPhone(p.phone);
                    setSendError("");
                  }}
                  className="accent-brand-500"
                />
                <span className="tabular-nums">{p.phone}</span>
                <span className="text-xs text-fg-muted truncate">
                  {p.source === "registered"
                    ? t("Registrado")
                    : p.alias
                    ? t("Agenda · {{alias}}").replace("{{alias}}", p.alias)
                    : t("Agenda")}
                </span>
              </label>
            ))}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
              <button type="button" onClick={() => setView(VIEW.ADD_NUMBER)} className={BTN_LINK}>
                {t("Agregar número")}
              </button>
              <button type="button" onClick={() => setView(VIEW.EDIT_PHONE)} className={BTN_LINK}>
                {t("Editar teléfono registrado")}
              </button>
            </div>
          </fieldset>
        )}

        {driver && view === VIEW.ADD_NUMBER && (
          <section aria-label={t("Agregar número")} className="rounded-md border border-border p-3">
            <AddNumberForm
              driverId={driver.id}
              dialCode={dialCode}
              onAdded={handleNumberAdded}
              onCancel={backToCompose}
            />
          </section>
        )}

        {driver && view === VIEW.EDIT_PHONE && (
          <section
            aria-label={t("Editar teléfono registrado")}
            className="rounded-md border border-border p-3"
          >
            <DriverPhoneEditor
              driverId={driver.id}
              onSaved={handlePhoneEdited}
              onCancel={backToCompose}
            />
          </section>
        )}

        <div>
          <label htmlFor={textId} className="block text-xs font-medium text-fg mb-1">
            {t("Mensaje")}
          </label>
          <Textarea
            id={textId}
            rows={5}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (sendError) setSendError("");
            }}
            disabled={sending}
            invalid={overLimit}
            aria-describedby={sendError ? `${counterId} ${errorId}` : counterId}
            aria-invalid={overLimit ? true : undefined}
            placeholder={t("Escribe el mensaje…")}
          />
          <p
            id={counterId}
            className={cn(
              "mt-1 text-right text-[11px] tabular-nums",
              overLimit ? "text-status-error" : "text-fg-muted",
            )}
          >
            {`${text.length}/${MAX_MESSAGE_LENGTH}`}
          </p>
        </div>

        {sendError && (
          <p id={errorId} role="alert" className="text-xs text-status-error">
            {sendError}
          </p>
        )}
      </div>
    </Modal>
  );
}
